// RecruitmentPage.jsx
// FY27 recruitment view built from the "Scheduling OIDS-TC Master" workbook:
// the Headcount (Yearly View) block of the "Recruitment Dashboard" sheet, then the
// funnel, splits and candidate lists of the "Recruitment Dashboard _ New" sheet.
// Admins refresh the data by importing the workbook from this page.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as XLSX from 'xlsx';

import PageHeading from '@/components/common/PageHeading';
import LoadingOverlay from '@/components/common/LoadingOverlay';
import HeadcountTable from '@/components/data/recruitment/HeadcountTable';
import StatusChart from '@/components/data/recruitment/StatusChart';
import SplitPie from '@/components/data/recruitment/SplitPie';
import CandidateList from '@/components/data/recruitment/CandidateList';

import useRedux from '@/hooks/useRedux';
import { getRecruitment, importRecruitment } from '@/services/schedulingService';
import parseRecruitmentWorkbook, { RECRUITMENT_SHEETS } from '@/utils/recruitment/parseRecruitmentWorkbook';
import {
  FISCAL_YEAR,
  PROCESS_MODES,
  SERVICE_LINES,
  STAGES,
  STATUSES,
  countBy,
  filterCandidates,
  stageOf,
  statusInfo,
  statusNumber
} from '@/utils/recruitment/recruitment';

import { recruitmentCss as css } from '@digital_monorepo/styling';

// Pill filters in the style of the Summary page's Overview; each pill shows how many candidates it would select.
const PillFilter = ({ id, label, options, value, onChange }) => (
  <div className={css.filter}>
    <span className={css.filterLabel} id={id}>{label}</span>
    <div className={css.pills} role='group' aria-labelledby={id}>
      {options.map(o => (
        <button key={o.key} type='button' aria-pressed={value === o.key} className={`${css.pill} ${value === o.key ? css.pillActive : ''}`} onClick={() => onChange(o.key)}>
          {o.label}
          <span className={css.pillCount}>{o.count}</span>
        </button>
      ))}
    </div>
  </div>
);

const NOT_SPECIFIED = 'Not specified';
const NEUTRAL = '#c9d3cd';
// Same colours as the Summary page's gender and office pies.
const GENDER_COLOURS = { Female: '#0d8390', Male: '#25634b' };
const PIE_COLOURS = ['#25634b', '#86bc25', '#0d8390', '#007cb0', '#43b02a', '#6fc2b4', '#9dd4cf', '#3c8a2e', '#a0dcff', '#62b5e5'];
const genderColour = label => GENDER_COLOURS[label] || NEUTRAL;
const pieColour = (label, index) => (label === NOT_SPECIFIED ? NEUTRAL : PIE_COLOURS[index % PIE_COLOURS.length]);

const formatImportedAt = value => value
  ? new Date(value).toLocaleString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  : null;

const RecruitmentPage = () => {
  const { t } = useTranslation();
  const { getAuthData } = useRedux();
  const isAdmin = getAuthData().role === 'ADMIN';

  const [data, setData] = useState({ candidates: [], headcount: [], importedAt: null });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [importState, setImportState] = useState({ busy: false, message: null, failed: false });

  const [mode, setMode] = useState('accumulated');
  const [serviceLine, setServiceLine] = useState('');
  // The candidate list has its own copy of the filters, plus status and name.
  const [listMode, setListMode] = useState('accumulated');
  const [listServiceLine, setListServiceLine] = useState('');
  const [listStatus, setListStatus] = useState(null);
  const [query, setQuery] = useState('');
  const [showDetail, setShowDetail] = useState(false);
  const [showList, setShowList] = useState(true);

  const fileInput = useRef(null);
  const listSection = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setData(await getRecruitment(FISCAL_YEAR));
      setError(false);
    } catch (err) {
      console.error('Error fetching recruitment data:', err);
      setError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleImport = async event => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setImportState({ busy: true, message: null, failed: false });
    try {
      const workbook = XLSX.read(await file.arrayBuffer(), { type: 'array', sheets: RECRUITMENT_SHEETS });
      const result = await importRecruitment(parseRecruitmentWorkbook(workbook));
      setImportState({ busy: false, message: `Imported ${result.candidates} candidates from ${file.name}.`, failed: false });
      await load();
    } catch (err) {
      console.error('Error importing recruitment workbook:', err);
      const reason = err?.response?.data?.details?.[0]?.message || err?.response?.data?.message || err.message;
      setImportState({ busy: false, message: `Import failed: ${reason}`, failed: true });
    }
  };

  // Funnel, stat cards, chart and pies follow the funnel filters.
  const scoped = useMemo(
    () => filterCandidates(data.candidates, { mode, serviceLine }),
    [data.candidates, mode, serviceLine]
  );

  const listScoped = useMemo(
    () => filterCandidates(data.candidates, { mode: listMode, serviceLine: listServiceLine }),
    [data.candidates, listMode, listServiceLine]
  );

  const listed = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return filterCandidates(listScoped, { status: listStatus })
      .filter(c => !needle || (c.CandidateName || '').toLowerCase().includes(needle));
  }, [listScoped, listStatus, query]);

  // Statuses offered by the list's status filter, grouped, with counts under its other filters.
  const statusOptions = useMemo(() => {
    const counts = new Map();
    listScoped.forEach(c => counts.set(c.Status, (counts.get(c.Status) || 0) + 1));
    const known = new Set(STATUSES.map(s => s.key));
    const extra = [...counts.keys()].filter(k => k && !known.has(k)).map(statusInfo);
    const groups = [];
    [...STATUSES, ...extra].forEach(s => {
      let group = groups.find(g => g.name === s.group);
      if (!group) groups.push(group = { name: s.group, statuses: [] });
      group.statuses.push({ ...s, count: counts.get(s.key) || 0 });
    });
    return groups;
  }, [listScoped]);

  // Selecting a status in the chart lists those candidates under the funnel's filters.
  const selectStatus = key => {
    setListMode(mode);
    setListServiceLine(serviceLine);
    setListStatus(key);
    setQuery('');
    if (key) setShowList(true);
    if (key) listSection.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const chartSelection = listMode === mode && listServiceLine === serviceLine ? listStatus : null;
  const listFiltered = listMode !== 'accumulated' || listServiceLine || listStatus || query;
  const clearListFilters = () => {
    setListMode('accumulated');
    setListServiceLine('');
    setListStatus(null);
    setQuery('');
  };

  // The total card plus one card per stage; stages are exclusive, so the cards add up to the total.
  const stages = useMemo(() => {
    const counts = new Map();
    scoped.forEach(c => { const key = stageOf(c.Status) || 'other'; counts.set(key, (counts.get(key) || 0) + 1); });
    const other = counts.get('other') ? [{ key: 'other', label: 'Other', hint: 'No recognised status' }] : [];
    return [...STAGES.map(st => ({ ...st, hint: st.from === st.to ? `Status ${st.from}` : `Status ${st.from}–${st.to}` })), ...other]
      .map(st => ({ ...st, count: counts.get(st.key) || 0 }));
  }, [scoped]);

  const modeOptions = useMemo(() => PROCESS_MODES.map(m => ({
    ...m, count: filterCandidates(data.candidates, { mode: m.key, serviceLine }).length
  })), [data.candidates, serviceLine]);

  const serviceLineOptions = useMemo(() => [{ key: '', label: 'All' }, ...SERVICE_LINES.map(l => ({ key: l, label: l }))].map(o => ({
    ...o, count: filterCandidates(data.candidates, { mode, serviceLine: o.key }).length
  })), [data.candidates, mode]);

  const listModeOptions = useMemo(() => PROCESS_MODES.map(m => ({
    ...m, count: filterCandidates(data.candidates, { mode: m.key, serviceLine: listServiceLine }).length
  })), [data.candidates, listServiceLine]);

  const listServiceLineOptions = useMemo(() => [{ key: '', label: 'All' }, ...SERVICE_LINES.map(l => ({ key: l, label: l }))].map(o => ({
    ...o, count: filterCandidates(data.candidates, { mode: listMode, serviceLine: o.key }).length
  })), [data.candidates, listMode]);

  const splits = useMemo(() => [
    { title: 'Gender', items: countBy(scoped, 'Gender', NOT_SPECIFIED), colours: genderColour },
    { title: 'Office', items: countBy(scoped, 'Office', NOT_SPECIFIED), colours: pieColour },
    { title: 'Experience', items: countBy(scoped, 'Experience', NOT_SPECIFIED), colours: pieColour }
  ], [scoped]);

  const modeLabel = PROCESS_MODES.find(m => m.key === mode).label;
  const isEmpty = !data.candidates.length && !data.headcount.length;

  return (
    <div className={css.pageContainer}>
      {(loading || importState.busy) && <LoadingOverlay />}
      <PageHeading title={t('HOMENAV.PAGES.RECRUITMENT')}>
        {data.importedAt && (
          <span className='workspace-count'><span className='status-dot' />{FISCAL_YEAR} · last import {formatImportedAt(data.importedAt)}</span>
        )}
        {isAdmin && (
          <>
            <input ref={fileInput} type='file' accept='.xlsm,.xlsx' hidden onChange={handleImport} />
            <button type='button' className='workspace-button workspace-button-secondary' disabled={importState.busy} onClick={() => fileInput.current?.click()}>
              Import workbook
            </button>
          </>
        )}
      </PageHeading>

      {importState.message && (
        <p className={`${css.notice} ${importState.failed ? css.noticeError : ''}`} role='status'>{importState.message}</p>
      )}

      {!loading && error && (
        <div className='workspace-empty'>
          <h2>Data could not be loaded</h2>
          <p>Try again in a moment.</p>
        </div>
      )}

      {!loading && !error && isEmpty && (
        <div className='workspace-empty'>
          <h2>No recruitment data yet</h2>
          <p>{isAdmin ? 'Use "Import workbook" to load the Scheduling OIDS-TC Master workbook.' : 'An administrator needs to import the recruitment workbook.'}</p>
        </div>
      )}

      {!loading && !error && !isEmpty && (
        <>
          <section className={css.section} aria-labelledby='headcount-title'>
            <header className={css.sectionHeader}>
              <div>
                <h2 id='headcount-title' className={css.sectionTitle}>
                  <span className={css.sectionIndicator} aria-hidden='true' />
                  Headcount (Yearly View)
                </h2>
                <p className={css.sectionSubtitle}>{FISCAL_YEAR} · June to May · current month highlighted</p>
              </div>
            </header>
            <HeadcountTable headcount={data.headcount} />
          </section>

          <section className={css.section} aria-labelledby='funnel-title'>
            <header className={css.sectionHeader}>
              <div>
                <h2 id='funnel-title' className={css.sectionTitle}>
                  <span className={css.sectionIndicator} aria-hidden='true' />
                  Recruitment funnel
                </h2>
                <p className={css.sectionSubtitle}>{FISCAL_YEAR} · candidates by current status</p>
              </div>
            </header>

            <div className={css.filters}>
              <PillFilter id='funnel-processes' label='Processes' options={modeOptions} value={mode} onChange={setMode} />
              <PillFilter id='funnel-service-line' label='Service line' options={serviceLineOptions} value={serviceLine} onChange={setServiceLine} />
            </div>

            <div className={css.stats}>
              <div className={`${css.stat} ${css.statTotal}`}>
                <span className={css.statLabel}>Candidates</span>
                <span className={css.statValue}>{scoped.length}</span>
                <span className={css.statHint}>{modeLabel} · {serviceLine || 'all service lines'}</span>
              </div>
              {stages.map(st => (
                <div key={st.key} className={`${css.stat} ${css.statSubset}`}>
                  <span className={css.statLabel}>{st.label}</span>
                  <span className={css.statValue}>{st.count}</span>
                  <span className={css.statHint}>
                    {scoped.length ? Math.round((st.count / scoped.length) * 100) : 0}% · {st.hint}
                  </span>
                </div>
              ))}
            </div>

            <div className={css.funnelGrid}>
              <StatusChart candidates={scoped} serviceLine={serviceLine} selectedKey={chartSelection} onSelect={selectStatus} />
              <div className={css.splits}>
                {splits.map(split => <SplitPie key={split.title} title={`${split.title} split`} items={split.items} colours={split.colours} />)}
              </div>
            </div>
          </section>

          <section ref={listSection} className={css.section} aria-labelledby='candidates-title'>
            <header className={css.sectionHeader}>
              <div>
                <h2 id='candidates-title' className={css.sectionTitle}>
                  <span className={css.sectionIndicator} aria-hidden='true' />
                  Candidates
                </h2>
                <p className={css.sectionSubtitle} aria-live='polite'>
                  {listed.length} of {data.candidates.length} candidates · {FISCAL_YEAR}
                </p>
              </div>
              <div className={css.headerActions}>
                {showList && (
                  <button
                    type='button'
                    className={`workspace-button ${showDetail ? 'workspace-button-secondary' : ''}`}
                    aria-pressed={showDetail}
                    onClick={() => setShowDetail(v => !v)}>
                    {showDetail ? 'Hide detail' : 'View detail'}
                  </button>
                )}
                <button
                  type='button'
                  className='workspace-button workspace-button-secondary'
                  aria-expanded={showList}
                  aria-controls='candidates-list'
                  onClick={() => setShowList(v => !v)}>
                  {showList ? 'Hide list' : 'Show list'}
                </button>
              </div>
            </header>

            {showList && (
              <div id='candidates-list'>
                <div className={css.filters}>
                  <PillFilter id='list-processes' label='Processes' options={listModeOptions} value={listMode} onChange={setListMode} />
                  <PillFilter id='list-service-line' label='Service line' options={listServiceLineOptions} value={listServiceLine} onChange={setListServiceLine} />
                  <label className={css.filter}>
                    <span className={css.filterLabel}>Status</span>
                    <select className={css.select} value={listStatus || ''} onChange={event => setListStatus(event.target.value || null)}>
                      <option value=''>All statuses ({listScoped.length})</option>
                      {statusOptions.map(group => (
                        <optgroup key={group.name} label={group.name}>
                          {group.statuses.map(s => (
                            <option key={s.key} value={s.key} disabled={!s.count && s.key !== listStatus}>
                              {statusNumber(s.key) ? `${statusNumber(s.key)} · ` : ''}{s.label} ({s.count})
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                  </label>
                  <label className={css.filter}>
                    <span className={css.filterLabel}>Name</span>
                    <input type='search' className={css.search} placeholder='Search candidates' value={query} onChange={event => setQuery(event.target.value)} />
                  </label>
                  {listFiltered && (
                    <button type='button' className={css.linkButton} onClick={clearListFilters}>Clear filters</button>
                  )}
                </div>

                <div className={css.card}>
                  <CandidateList candidates={listed} detail={showDetail} />
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default RecruitmentPage;
